// searchEventHandler.ts
import { browser } from "$app/environment";
import { goto } from "$app/navigation";
import { SearchAPI } from "$lib/api/search";
import { searchHistoryStore } from "$lib/stores";
import type { SearchInfo, SearchResult, SearchHistoryRecord } from "$lib/types";
import { SEARCH_PROGRESS, MATCH_STATUS } from "$lib/types";
import type { StoreUpdateFunction, StoreSaveFunction, ResultProcessFunction, SlugUpdateResult } from "./types";

export interface EventHandlerConfig {
  eventSource: EventSource;
  slug: string;
  initialSlug?: string;
  updateStoreFn: StoreUpdateFunction;
  endSearchFn: (slug: string) => void;
  saveSearchToLocalStorage: StoreSaveFunction;
  processSearchResult: ResultProcessFunction;
  activeStreams: Record<string, EventSource>;
}

export class SearchEventHandler {
  private static readonly SEARCH_PREFIX = "search_";
  private static readonly DEFAULT_MODEL = { value: "gpt-4o-mini", label: "gpt-4o-mini" };

  /**
   * Sets up event handlers for the main search process
   */
  static setupSearchEventHandlers(config: EventHandlerConfig): SlugUpdateResult {
    let currentSlugValue = config.slug;

    logger.log("Setting up search event handlers for:", currentSlugValue);
    logger.log("Config:", config);

    const { eventSource } = config;

    eventSource.onopen = () => {
      logger.log("SSE connection opened for search:", config.initialSlug || config.slug);
      // Add explicit status update on connection open
      config.updateStoreFn((states) => ({
        ...states,
        [currentSlugValue]: {
          ...states[currentSlugValue],
          progressStatus: SEARCH_PROGRESS.IN_PROGRESS,
        },
      }));
    };

    eventSource.onmessage = (event) =>
      this.handleSearchMessage(event, config, currentSlugValue, (newSlug) => {
        currentSlugValue = newSlug;
      });

    eventSource.onerror = (error) => {
      this.handleSearchError(error, config);
      eventSource.close();
    };

    eventSource.addEventListener("end", (event: Event) => {
      logger.log("[basic] End event received for:", currentSlugValue);
      logger.log("End event data:", event);

      const processEndAndCleanup = () => {
        config.updateStoreFn((states) => {
          const currentState = states[currentSlugValue];
          if (!currentState) {
            console.warn("No state found for slug during end event:", currentSlugValue);
            return states;
          }

          logger.log("Updating state to FINISHED for:", currentSlugValue);

          const updatedState = {
            ...currentState,
            findMore: true,
            totalResultsFound: this.getTotalMatchingResults(currentState.results || []),
            totalResultsGenerated: (currentState.results || []).length,
          };

          try {
            config.saveSearchToLocalStorage(updatedState);
          } catch (error) {
            console.error("Failed to save final state:", error);
          }

          return { ...states, [currentSlugValue]: updatedState };
        });

        config.endSearchFn(currentSlugValue);

        logger.log("Cleaning up event source for:", currentSlugValue);
        config.eventSource.close();
        delete config.activeStreams[currentSlugValue];
      };

      // Ensure the end processing happens synchronously
      processEndAndCleanup();
    });

    return {
      getCurrentSlug: () => currentSlugValue,
      updateCurrentSlug: (newSlug: string) => {
        currentSlugValue = newSlug;
      },
    };
  }

  private static handleSlugUpdate(
    data: { match: number; linkedinURL: string },
    currentSlug: string,
    config: EventHandlerConfig,
    updateSlug: (newSlug: string) => void,
  ): void {
    const { match: searchID, linkedinURL: newSlug } = data;

    if (newSlug === currentSlug) return;

    // Update stores first
    this.updateSlugInStores(
      currentSlug,
      newSlug,
      searchID.toString(),
      config.updateStoreFn,
      config.saveSearchToLocalStorage,
    );

    // Update the slug after store update
    updateSlug(newSlug);

    // Update active searches
    if (config.activeStreams[currentSlug]) {
      config.activeStreams[newSlug] = config.activeStreams[currentSlug];
      delete config.activeStreams[currentSlug];
    }

    logger.log(`Slug updated to ${newSlug}, search ID set to ${searchID}`);

    // Ensure status is maintained after slug update
    config.updateStoreFn((states) => ({
      ...states,
      [newSlug]: {
        ...states[newSlug],
        progressStatus: SEARCH_PROGRESS.IN_PROGRESS,
      },
    }));

    if (browser) {
      const pathname = window.location.pathname;
      const basePath = pathname.startsWith("/search-dev/") ? "/search-dev/" : "/search/";
      // goto(`${basePath}${newSlug}`, { replaceState: true });
      window.location.href = `${basePath}${newSlug}`;
    }
  }

  private static handleSearchEndAndCleanup(
    config: EventHandlerConfig,
    currentSlug: string,
    options: { findMore: boolean } = { findMore: false },
  ): void {
    logger.log("Processing search end and cleanup for:", currentSlug);

    // First update the store
    config.updateStoreFn((states) => {
      const currentState = states[currentSlug];
      if (!currentState) {
        console.warn("No state found for slug during end event:", currentSlug);
        return states;
      }

      const updatedState: SearchInfo = {
        ...currentState,
        progressStatus: SEARCH_PROGRESS.FINISHED, // Explicitly set status
        findMore: options.findMore,
        totalResultsFound: this.getTotalMatchingResults(currentState.results || []),
        totalResultsGenerated: (currentState.results || []).length,
      };

      try {
        // Save to localStorage before completing
        config.saveSearchToLocalStorage(updatedState);
        logger.log("Search state saved to localStorage:", currentSlug);

        // Update history store
        this.updateSearchHistoryStore(currentSlug, {
          matchesCount: updatedState.totalResultsFound,
          status: SEARCH_PROGRESS.FINISHED, // Ensure history is updated too
        });
      } catch (error) {
        console.error("Failed to save search state:", error);
      }

      return { ...states, [currentSlug]: updatedState };
    });

    // Then cleanup the event source
    try {
      logger.log("Cleaning up event source for:", currentSlug);
      config.eventSource.close();
      delete config.activeStreams[currentSlug];
    } catch (error) {
      console.error("Error during cleanup:", error);
    }
  }

  private static handleSearchError(error: Event, config: EventHandlerConfig): void {
    console.error("Error with SSE:", error);

    // Update store with CANCELLED status
    config.updateStoreFn((states) => {
      const currentState = states[config.slug];
      if (!currentState) return states;

      const error = "Search encountered an error";

      // // TODO: Handle 402 instead of assuming search limit here
      // if (currentState.results.length === 0 && currentState.mode == "pro") {
      //   searchHistoryStore.removeSearch(config.slug);
      //   // error = "You've reached the limit of Pro searches.";
      // }

      const finalState = {
        ...currentState,
        progressStatus: SEARCH_PROGRESS.CANCELLED,
        error: error,
        findMore: false,
      };

      try {
        config.saveSearchToLocalStorage(finalState);
      } catch (saveError) {
        console.error("Error saving cancelled state:", saveError);
      }

      return {
        ...states,
        [config.slug]: finalState,
      };
    });

    // Cleanup after state update
    try {
      config.eventSource.close();
      delete config.activeStreams[config.slug];
    } catch (cleanupError) {
      console.error("Error during error cleanup:", cleanupError);
    }
  }

  /**
   * Handles messages during the main search process
   */
  private static handleSearchMessage(
    event: MessageEvent,
    config: EventHandlerConfig,
    currentSlug: string,
    updateSlug: (newSlug: string) => void,
  ): void {
    try {
      // logger.log("RAW Search message:", event.data);

      if (this.isHeartbeatMessage(event.data)) {
        logger.log("Treating as heartbeat");
        return;
      }

      const jsonData = SearchAPI.decodeSearchResult(event.data);
      logger.log("Decoded search result:", jsonData);

      if (this.isSlugUpdateMessage(jsonData)) {
        logger.log("Handling slug update");
        this.handleSlugUpdate(jsonData, currentSlug, config, updateSlug);
        return;
      }

      config.processSearchResult(currentSlug, jsonData as SearchResult);
    } catch (error) {
      console.error("Error processing result:", error, "Raw event data:", event.data);
    }
  }

  /**
   * Handles the end of a search operation
   */
  // private static handleSearchEnd(config: EventHandlerConfig, currentSlug: string): void {
  //   logger.log("Processing search end for:", currentSlug);

  //   config.updateStoreFn((states) => {
  //     const currentState = states[currentSlug];
  //     if (!currentState) {
  //       console.warn("No state found for slug during end event:", currentSlug);
  //       return states;
  //     }

  //     const updatedState: SearchInfo = {
  //       ...currentState,
  //       progressStatus: SEARCH_PROGRESS.FINISHED,
  //       totalResultsFound: this.getTotalMatchingResults(currentState.results || []),
  //       totalResultsGenerated: (currentState.results || []).length,
  //     };

  //     try {
  //       config.saveSearchToLocalStorage(updatedState);
  //       logger.log("Search state saved to localStorage:", currentSlug);
  //     } catch (error) {
  //       console.error("Failed to save search state to localStorage:", error);
  //     }

  //     this.updateSearchHistoryStore(currentSlug, {
  //       matchesCount: updatedState.totalResultsFound,
  //     });

  //     return { ...states, [currentSlug]: updatedState };
  //   });

  //   this.cleanupEventSource(config.eventSource, config.activeStreams, currentSlug);
  // }
  // private static handleSearchEnd(
  //   config: EventHandlerConfig,
  //   currentSlug: string,
  //   options: { findMore: boolean } = { findMore: false },
  // ): void {
  //   logger.log("Processing search end for:", currentSlug);

  //   config.updateStoreFn((states) => {
  //     const currentState = states[currentSlug];
  //     if (!currentState) {
  //       console.warn("No state found for slug during end event:", currentSlug);
  //       return states;
  //     }

  //     const updatedState: SearchInfo = {
  //       ...currentState,
  //       progressStatus: SEARCH_PROGRESS.FINISHED,
  //       findMore: options.findMore,
  //       totalResultsFound: this.getTotalMatchingResults(currentState.results || []),
  //       totalResultsGenerated: (currentState.results || []).length,
  //     };

  //     try {
  //       config.saveSearchToLocalStorage(updatedState);
  //       logger.log("Search state saved to localStorage:", currentSlug);
  //     } catch (error) {
  //       console.error("Failed to save search state to localStorage:", error);
  //     }

  //     this.updateSearchHistoryStore(currentSlug, {
  //       matchesCount: updatedState.totalResultsFound,
  //     });

  //     return { ...states, [currentSlug]: updatedState };
  //   });
  // }

  /**
   * Updates the search history store
   */
  private static updateSearchHistoryStore(slug: string, update: SearchHistoryRecord): void {
    searchHistoryStore.updateSearch(slug, update);
  }

  /**
   * Gets total matching results
   */
  private static getTotalMatchingResults(results: SearchResult[]): number {
    return results.filter((r) => r.match === MATCH_STATUS.DEFINITE_MATCH || r.match === MATCH_STATUS.PARTIAL_MATCH)
      .length;
  }

  /**
   * Updates slug in stores and handles related state changes
   */
  // private static handleSlugUpdate(
  //   data: { match: number; linkedinURL: string },
  //   currentSlug: string,
  //   config: EventHandlerConfig,
  //   updateSlug: (newSlug: string) => void,
  // ): void {
  //   const { match: searchID, linkedinURL: newSlug } = data;

  //   if (newSlug === currentSlug) return;

  //   this.updateSlugInStores(
  //     currentSlug,
  //     newSlug,
  //     searchID.toString(),
  //     config.updateStoreFn,
  //     config.saveSearchToLocalStorage,
  //   );

  //   updateSlug(newSlug);

  //   if (config.activeStreams[currentSlug]) {
  //     config.activeStreams[newSlug] = config.activeStreams[currentSlug];
  //     delete config.activeStreams[currentSlug];
  //   }

  //   logger.log(`Slug updated to ${newSlug}, search ID set to ${searchID}`);

  //   if (browser) {
  //     goto(`/search/${newSlug}`, { replaceState: true });
  //   }
  // }

  /**
   * Updates slug-related information in various stores
   */
  private static updateSlugInStores(
    oldSlug: string,
    newSlug: string,
    searchID: string,
    updateStoreFn: StoreUpdateFunction,
    saveSearchToLocalStorage: StoreSaveFunction,
  ): void {
    const savedState = this.getSavedState(oldSlug);

    updateStoreFn((states) => {
      const state = states[oldSlug];
      if (!state) return states;

      const updatedState = this.createUpdatedState(state, newSlug, searchID, savedState);

      // Remove old state
      const { [oldSlug]: _, ...rest } = states;

      // Save new state to localStorage
      saveSearchToLocalStorage(updatedState);

      if (browser) {
        const pathname = window.location.pathname;
        const basePath = pathname.startsWith("/search-dev/") ? "/search-dev/" : "/search/";
        goto(`${basePath}${newSlug}`, { replaceState: true });
      }

      // Return new state with updated slug
      return { ...rest, [newSlug]: updatedState };
    });

    // Update history store
    this.updateSearchHistoryStore(oldSlug, {
      slug: newSlug,
      id: searchID,
    });
  }

  /**
   * Creates an updated state object with new slug information
   */
  private static createUpdatedState(
    currentState: SearchInfo,
    newSlug: string,
    searchID: string,
    savedState: Partial<SearchInfo> | null,
  ): SearchInfo {
    const updatedState = {
      ...currentState,
      slug: newSlug,
      id: searchID,
    };

    if (savedState) {
      updatedState.results = savedState.results || updatedState.results;
      // updatedState.totalLLMCost = savedState.totalLLMCost || updatedState.totalLLMCost;
      // updatedState.progressStatus = savedState.status || updatedState.status;
    }

    return updatedState;
  }

  /**
   * Common error handling for search operations
   */
  // private static handleSearchError(error: Event, config: EventHandlerConfig): void {
  //   console.error("Error with SSE:", error);

  //   config.updateStoreFn((states) => {
  //     const currentState = states[config.slug];
  //     if (!currentState) return states;

  //     const finalState = this.createErrorState(currentState);
  //     config.saveSearchToLocalStorage(finalState);

  //     this.updateSearchHistoryStore(config.slug, {
  //       progressStatus: SEARCH_PROGRESS.CANCELLED,
  //     });

  //     return {
  //       ...states,
  //       [config.slug]: finalState,
  //     };
  //   });

  //   this.cleanupEventSource(config.eventSource, config.activeStreams, config.slug);
  // }

  /**
   * Creates error state for failed searches
   */
  private static createErrorState(currentState: SearchInfo): SearchInfo {
    console.error("Setting error state for:", currentState.slug);
    return {
      ...currentState,
      progressStatus: SEARCH_PROGRESS.CANCELLED,
      findMore: currentState.findMore || false,
    };
  }

  /**
   * Type guard for slug update messages
   */
  private static isSlugUpdateMessage(data: unknown): data is { match: number; linkedinURL: string } {
    if (typeof data !== "object" || data === null) {
      return false;
    }

    const typedData = data as Record<string, unknown>;
    const hasNonEmptyFields = Object.entries(typedData).filter(
      ([_, value]) => value !== null && value !== undefined && value !== "",
    );

    return (
      "match" in data &&
      "linkedinURL" in data &&
      typeof typedData.linkedinURL === "string" &&
      typedData.linkedinURL !== "" &&
      !isNaN(Number(typedData.match)) &&
      hasNonEmptyFields.length === 2
    );
  }

  /**
   * Type guard for EventSource heartbeat messages
   * Validates raw event data in the format: {"message": "Processing..."}
   */
  private static isHeartbeatMessage(data: unknown): data is { message: string } {
    // Handle string input from EventSource
    if (typeof data === "string") {
      try {
        const parsed = JSON.parse(data);
        return (
          typeof parsed === "object" &&
          parsed !== null &&
          Object.keys(parsed).length === 1 &&
          "message" in parsed &&
          typeof parsed.message === "string"
        );
      } catch {
        return false;
      }
    }

    // Handle already parsed object
    if (typeof data === "object" && data !== null) {
      return (
        Object.keys(data).length === 1 &&
        "message" in data &&
        typeof (data as { message: unknown }).message === "string"
      );
    }

    return false;
  }
  /**
   * Retrieves saved state from localStorage
   */
  private static getSavedState(slug: string): Partial<SearchInfo> | null {
    if (!browser) return null;

    try {
      const savedData = localStorage.getItem(`${this.SEARCH_PREFIX}${slug}`);
      if (savedData) {
        const state = JSON.parse(savedData);
        localStorage.removeItem(`${this.SEARCH_PREFIX}${slug}`);
        return state;
      }
    } catch (error) {
      console.error("Error reading from localStorage during slug update:", error);
    }
    return null;
  }

  /**
   * Saves state to localStorage with quota handling
   */
  private static saveStateToStorage(slug: string, state: SearchInfo): void {
    if (!browser) return;

    try {
      localStorage.setItem(`${this.SEARCH_PREFIX}${slug}`, JSON.stringify(state));
    } catch (error) {
      // console.error("Error saving to localStorage:", error);
      if (error instanceof DOMException && error.name === "QuotaExceededError") {
        this.handleStorageQuotaExceeded(state);
      }
    }
  }

  /**
   * Cleans up EventSource and related resources
   */
  private static cleanupEventSource(
    eventSource: EventSource,
    activeStreams: Record<string, EventSource>,
    slug: string,
  ): void {
    logger.log("Cleaning up event source for:", slug);
    eventSource.close();
    delete activeStreams[slug];
  }

  /**
   * Handles storage quota exceeded error
   */
  private static handleStorageQuotaExceeded(state: SearchInfo): void {
    if (!browser) return;

    this.getStorageKeysOrderedByTime().some((key) => this.attemptToSaveAfterRemoval(key, state));
  }

  /**
   * Gets storage keys ordered by timestamp
   */
  private static getStorageKeysOrderedByTime(): string[] {
    return Object.keys(localStorage)
      .filter((key) => key.startsWith(this.SEARCH_PREFIX))
      .sort((a, b) => {
        const timeA = this.getStorageItemTimestamp(a);
        const timeB = this.getStorageItemTimestamp(b);
        return timeA - timeB;
      });
  }

  /**
   * Gets timestamp from storage item
   */
  private static getStorageItemTimestamp(key: string): number {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item).updatedAt || 0 : 0;
  }

  /**
   * Attempts to save state after removing an old item
   */
  private static attemptToSaveAfterRemoval(key: string, state: SearchInfo): boolean {
    localStorage.removeItem(key);
    try {
      localStorage.setItem(`${this.SEARCH_PREFIX}${state.slug}`, JSON.stringify(state));
      return true; // Successfully saved after removing old item
    } catch {
      return false; // Still not enough space, continue removing
    }
  }
}
